import type { Icon, IconWeight } from "@phosphor-icons/react";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import { type ButtonSize, type ButtonVariant, buttonStyles } from "./Button";
import { Kbd } from "./Kbd";
import { Tip } from "./Tooltip";

export interface IconButtonProps extends Omit<AriaButtonProps, "className" | "children" | "aria-label"> {
  /** Required: an icon alone is not an accessible name. Also shown as the tooltip. */
  "aria-label": string;
  icon: Icon;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  /** Hide the tooltip when the surrounding UI already labels the button. */
  noTooltip?: boolean;
  /** Defaults: 16px for sm and md, 18px for lg. */
  iconSize?: number;
  iconWeight?: IconWeight;
  /** Tooltip placement, e.g. "right" for buttons in the collapsed sidebar rail. */
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  /** Shortcut shown in the tooltip next to the label, e.g. ["mod", "K"]. Visual only. */
  shortcut?: string[];
}

export function IconButton({
  icon: IconComponent,
  variant = "ghost",
  size = "md",
  className,
  noTooltip,
  iconSize,
  iconWeight,
  tooltipPlacement,
  shortcut,
  ...props
}: IconButtonProps) {
  const button = (
    <AriaButton className={buttonStyles({ variant, size, iconOnly: true, className })} {...props}>
      <IconComponent size={iconSize ?? (size === "lg" ? 18 : 16)} weight={iconWeight} aria-hidden="true" />
    </AriaButton>
  );
  return noTooltip ? (
    button
  ) : (
    <Tip
      label={
        shortcut ? (
          <>
            <span>{props["aria-label"]}</span>
            <Kbd keys={shortcut} tone="inverse" />
          </>
        ) : (
          props["aria-label"]
        )
      }
      placement={tooltipPlacement}
    >
      {button}
    </Tip>
  );
}
